/**
 * PolicySignatureAlert Component
 * Shows a persistent alert for employees who haven't signed company policies
 */
import { useState, useEffect } from 'react';
import { AlertTriangle, FileText, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import CompanyPoliciesModal from './CompanyPoliciesModal';

const PolicySignatureAlert = () => {
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkPolicyStatus();
  }, []);

  const checkPolicyStatus = async () => {
    try {
      const response = await api.get('/policies/status');
      if (response.data.requires_signature && response.data.is_employee) {
        setRequiresSignature(true);
        // Auto-show modal on first load if not signed
        const hasSeenAlert = sessionStorage.getItem('policy_alert_seen');
        if (!hasSeenAlert) {
          sessionStorage.setItem('policy_alert_seen', 'true');
          // Show modal after a short delay
          setTimeout(() => setShowModal(true), 2000);
        }
      }
    } catch (error) {
      console.error('Failed to check policy status:', error);
    }
  };

  const handleSigned = () => {
    setRequiresSignature(false);
    setShowModal(false);
  };

  if (!requiresSignature || dismissed) return null;

  return (
    <>
      {/* Persistent Alert Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg" data-testid="policy-alert-banner">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-white/20 rounded-full">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold">Action Required: Sign Company Policies</p>
              <p className="text-sm text-white/90">You must read and sign the company policies to continue. This is mandatory for all employees.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowModal(true)}
              className="bg-white text-amber-600 hover:bg-amber-50 font-medium"
              data-testid="open-policies-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Read & Sign Now
            </Button>
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              title="Dismiss temporarily (will reappear)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Spacer to prevent content from being hidden behind banner */}
      <div className="h-16" />

      {/* Policy Modal */}
      <CompanyPoliciesModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSigned={handleSigned}
      />
    </>
  );
};

export default PolicySignatureAlert;
